/**
 * Syndication list page - Phase 7 Investor Syndication Layer.
 * Lists all syndication rounds with filters and progress overview.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2, Plus, Layers3, Banknote, Calendar } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import syndicationService from '../services/syndicationService';
import {
  SyndicationRound,
  SyndicationStatus,
} from '../types/syndication';

const STATUS_OPTIONS: Array<{ value: '' | SyndicationStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'soft_closing', label: 'Soft closing' },
  { value: 'hard_closing', label: 'Hard closing' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const statusBadge = (s: SyndicationStatus): string => {
  switch (s) {
    case 'draft':
      return 'bg-gray-100 text-gray-700 border-gray-300';
    case 'open':
      return 'bg-green-100 text-green-700 border-green-300';
    case 'soft_closing':
      return 'bg-amber-100 text-amber-700 border-amber-300';
    case 'hard_closing':
      return 'bg-orange-100 text-orange-700 border-orange-300';
    case 'closed':
      return 'bg-blue-100 text-blue-700 border-blue-300';
    case 'cancelled':
      return 'bg-red-100 text-red-700 border-red-300';
  }
};

export const formatCurrency = (n?: number | null): string => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const formatDate = (s?: string | null): string =>
  s ? new Date(s).toLocaleDateString() : '—';

const SyndicationPage: React.FC = () => {
  const [rounds, setRounds] = useState<SyndicationRound[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'' | SyndicationStatus>('');
  const [startupFilter, setStartupFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string>('');

  const load = async () => {
    setLoading(true);
    const res = await syndicationService.fetchRounds({
      status: statusFilter === '' ? undefined : statusFilter,
      startup_id: startupFilter ? Number(startupFilter) : undefined,
      project_id: projectFilter ? Number(projectFilter) : undefined,
      size: 100,
    });
    setLoading(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) setRounds(res.data.rounds);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, startupFilter, projectFilter]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center">
              <Layers3 className="w-6 h-6 mr-2 text-blue-600" />
              Syndication
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage syndication rounds, commitments, allocations, and SPV
              formation.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/syndication/wires"
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              <Banknote className="w-4 h-4 mr-2" />
              Wire tracker
            </Link>
            <Link
              to="/syndication/new"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Round
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as '' | SyndicationStatus)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Startup ID
            </label>
            <input
              type="number"
              value={startupFilter}
              onChange={(e) => setStartupFilter(e.target.value)}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Project ID
            </label>
            <input
              type="number"
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading rounds...
          </div>
        ) : rounds.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
            <Layers3 className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="mb-4">No syndication rounds yet.</p>
            <Link
              to="/syndication/new"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create your first round
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rounds.map((r) => {
              const committed = r.committed_amount ?? 0;
              const target = r.target_amount || 1;
              const pct = Math.min(100, Math.round((committed / target) * 100));
              const linkedName =
                r.project?.project_name ||
                r.startup?.name ||
                (r.project_id
                  ? `Project #${r.project_id}`
                  : r.startup_id
                  ? `Startup #${r.startup_id}`
                  : '—');
              const commitmentCount =
                r.commitment_count ?? r.commitments?.length ?? 0;
              return (
                <Link
                  key={r.id}
                  to={`/syndication/${r.id}`}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">
                        {r.round_name}
                      </h3>
                      <p className="text-xs text-gray-600 truncate mt-0.5">
                        {linkedName}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded border text-xs font-medium capitalize flex-shrink-0 ${statusBadge(
                        r.status
                      )}`}
                    >
                      {r.status.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>
                        {formatCurrency(committed)} committed
                      </span>
                      <span>{formatCurrency(r.target_amount)} target</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 bg-blue-500 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {pct}%
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {commitmentCount} commitment
                      {commitmentCount === 1 ? '' : 's'}
                    </span>
                    {r.close_date && (
                      <span className="inline-flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(r.close_date)}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SyndicationPage;
